/**
 * Commerce · Orders.
 */
import { useMemo, useState } from "react";
import { ShoppingBag, Sparkles } from "lucide-react";
import { Card, SectionHeader, TextInput, Button, SeverityBadge } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { useDeepLink } from "../../lib/integration";
import { ORDERS, OrderStatus, OrderChannel, Order } from "../../mocks/commerceData";

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: "Pending", paid: "Paid", packed: "Packed", shipped: "Shipped",
  delivered: "Delivered", refunded: "Refunded", cancelled: "Cancelled",
};
const STATUS_TINT: Record<OrderStatus, { bg: string; fg: string }> = {
  pending:   { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  paid:      { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  packed:    { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  shipped:   { bg: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  delivered: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  refunded:  { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"      },
  cancelled: { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"        },
};

const severityFor = (o: Order): "high" | "medium" | "low" => {
  if (o.paymentMethod === "cash-on-delivery" && o.status === "pending") return "high";
  if (o.totalEGP >= 50000) return "high";
  if (o.status === "refunded" || o.status === "cancelled") return "medium";
  return "low";
};

export default function Orders() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab]   = useState<OrderStatus | "all">("all");
  const [channel, setChannel] = useState<OrderChannel | "all">("all");
  const [q, setQ]       = useState(initialQ);
  const [selected, setSelected] = useState<Order | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: ORDERS.length };
    ORDERS.forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => ORDERS.filter((o) =>
      (tab === "all" || o.status === tab) &&
      (channel === "all" || o.channel === channel) &&
      (!q || o.reference.toLowerCase().includes(q.toLowerCase()) || o.customer.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, channel, q],
  );

  return (
    <div className="space-y-6" data-testid="cm-orders">
      <Card testId="cm-orders-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search by order # or customer…" testId="cm-orders-search" /></div>
          <Segmented<OrderChannel | "all">
            testId="cm-orders-channel"
            value={channel}
            onChange={setChannel}
            options={[
              { value: "all",       label: "All"       },
              { value: "web",       label: "Web"       },
              { value: "pop-up",    label: "Pop-up"    },
              { value: "wholesale", label: "Wholesale" },
              { value: "affiliate", label: "Affiliate" },
            ]}
          />
        </div>
        <div className="mt-4">
          <TabBar<OrderStatus | "all">
            testId="cm-orders-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",       label: "All",       count: counts.all       },
              { key: "pending",   label: "Pending",   count: counts.pending   },
              { key: "paid",      label: "Paid",      count: counts.paid      },
              { key: "packed",    label: "Packed",    count: counts.packed    },
              { key: "shipped",   label: "Shipped",   count: counts.shipped   },
              { key: "delivered", label: "Delivered", count: counts.delivered },
              { key: "refunded",  label: "Refunded",  count: counts.refunded  },
              { key: "cancelled", label: "Cancelled", count: counts.cancelled },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="cm-orders-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader
            eyebrow="Queue"
            title={`${filtered.length} order${filtered.length === 1 ? "" : "s"}`}
            action={<ShoppingBag size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
        </div>
        <div className="p-2" data-testid="cm-orders-list">
          {filtered.map((o) => {
            const tint = STATUS_TINT[o.status];
            return (
              <ListRow
                key={o.id}
                testId={`cm-order-${o.id}`}
                onClick={() => setSelected(o)}
                label={
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{o.reference}</span>
                    <span>{o.customer}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase flex-shrink-0"
                      style={{ letterSpacing: "0.14em", background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {STATUS_LABEL[o.status]}
                    </span>
                    {o.aiFlag && (
                      <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: "var(--ph-ai)" }}>
                        <Sparkles size={9} /> {o.aiFlag}
                      </span>
                    )}
                  </span>
                }
                sublabel={<>{o.city} · {o.channel} · {o.placedRelative} · {o.lines.length} line{o.lines.length === 1 ? "" : "s"}</>}
                trailing={
                  <>
                    <span className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                      EGP {o.totalEGP.toLocaleString()}
                    </span>
                    <SeverityBadge severity={severityFor(o)} />
                  </>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Order" testId="cm-order-drawer" width={480}>
        {selected && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>{selected.reference}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">
              {selected.customer}
            </div>
            <Field label="Status" inline>
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase"
                style={{ letterSpacing: "0.14em", background: STATUS_TINT[selected.status].bg, color: STATUS_TINT[selected.status].fg, borderRadius: "var(--ph-radius-sm)" }}>
                {STATUS_LABEL[selected.status]}
              </span>
            </Field>
            <Field label="Channel" inline><span className="text-sm">{selected.channel}</span></Field>
            <Field label="City" inline><span className="text-sm">{selected.city}</span></Field>
            <Field label="Payment" inline><span className="text-sm">{selected.paymentMethod}</span></Field>
            <Field label="Placed" inline><span className="text-sm">{selected.placedRelative}</span></Field>
            {selected.aiFlag && (
              <Field label="AI signal" inline>
                <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--ph-ai)" }}>
                  <Sparkles size={11} /> {selected.aiFlag}
                </span>
              </Field>
            )}
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Line items</div>
              <div className="space-y-1.5">
                {selected.lines.map((ln, i) => (
                  <div key={i} className="flex items-center justify-between p-2" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-sm)" }}>
                    <div className="min-w-0">
                      <div className="text-xs font-mono" style={{ color: "var(--ph-ink-muted)" }}>{ln.sku}</div>
                      <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{ln.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{ln.qty} × EGP {ln.unitEGP.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>Total</div>
                <div className="text-lg font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {selected.totalEGP.toLocaleString()}</div>
              </div>
            </div>
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="cm-order-refund">Issue refund</Button>
              <Button testId="cm-order-fulfill">Advance status</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
